import React, { useEffect, useState } from "react";
import Card from "../../components/Card";
import Table from "../../components/Table";
import { useUserSensorReadingService } from "../../services/sensorForuser";
import { useNavigate } from "react-router-dom";

const isNumber = (v) => typeof v === "number" && Number.isFinite(v);
const formatTs = (ts) => {
  if (!ts) return "-";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

const Borewell = () => {
  const { getUserSensorReadings } = useUserSensorReadingService();
  const [sensorData, setSensorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let intervalId;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getUserSensorReadings({
          summary: true,
          onlyWithData: true,
        });
        setSensorData(response.data || []);
      } catch (err) {
        setError("Failed to fetch sensor readings");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    intervalId = setInterval(fetchData, 60000);
    return () => clearInterval(intervalId);
  }, []);

  // DWLR: show sensors that provide waterLevel
  const dwlrData = sensorData
    .filter((r) => isNumber(r.waterLevel))
    .map((reading) => ({
      title: `${reading.monitoringUnitId} • ${reading.sensorId}`,
      value: reading.waterLevel,
      unit: "m",
      timestamp: reading.readingTimestamp || reading.updatedAt,
    }));

  // Flowmeter/Telemetry: show sensors that provide flow fields
  const flowmeterData = sensorData
    .filter((r) => isNumber(r.currentFlow) || isNumber(r.totalFlow))
    .map((reading) => [
      ...(isNumber(reading.totalFlow)
        ? [
            {
              title: `${reading.monitoringUnitId} • ${reading.sensorId} — Total Flow`,
              value: reading.totalFlow,
              unit: "m3",
              timestamp: reading.readingTimestamp || reading.updatedAt,
            },
          ]
        : []),
      ...(isNumber(reading.currentFlow)
        ? [
            {
              title: `${reading.monitoringUnitId} • ${reading.sensorId} — Current Flow`,
              value: reading.currentFlow,
              unit: "m3/h",
              timestamp: reading.readingTimestamp || reading.updatedAt,
            },
          ]
        : []),
    ])
    .flat();

  const historyColumns = [
    { key: "timestamp", title: "Timestamp" },
    { key: "parameter", title: "Parameter" },
    { key: "value", title: "Value" },
    { key: "unit", title: "Unit" },
    { key: "status", title: "Status" },
  ];

  // Example: Map history from sensorData (adjust as needed)
  const historyData = sensorData.map((reading) => ({
    timestamp: formatTs(reading.readingTimestamp || reading.updatedAt),
    parameter: `${reading.monitoringUnitId} • ${reading.sensorId}`,
    value:
      (isNumber(reading.waterLevel) && `${reading.waterLevel} m`) ||
      (isNumber(reading.currentFlow) && `${reading.currentFlow} m3/h`) ||
      (isNumber(reading.totalFlow) && `${reading.totalFlow} m3`) ||
      "—",
    unit: "-",
    status: reading.status || "Normal",
  }));

  // Table columns for summary
  const summaryColumns = [
    { key: "monitoringUnitId", title: "Monitoring Unit ID" },
    { key: "sensorId", title: "Sensor ID" },
    {
      key: "waterLevel",
      title: "Water Level",
      render: (v) => (v !== null ? v + " m" : "-"),
    },
    {
      key: "totalFlow",
      title: "Total Flow",
      render: (v) => (v !== null && v !== undefined ? v + " m3" : "-"),
    },
    {
      key: "currentFlow",

      title: "Current Flow",
      render: (v) => (v !== null && v !== undefined ? v + " m3/h" : "-"),
    },
    {
      key: "updatedAt",
      title: "Timestamp",
      render: (v) => (v ? new Date(v).toLocaleString() : "-"),
    },
  ];

  const handleRowClick = (row) => {
    if (row?._id) {
      navigate(`/client/sensor-readings/${row._id}`);
      return;
    }
    navigate("/client/borewell/detail", { state: { sensorData: row } });
  };

  if (loading) {
    return <div className="p-6">Loading sensor data...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-600">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Sensors Monitoring</h1>
        <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          Export Data
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Sensors Summary</h2>
        <Table
          columns={summaryColumns}
          data={sensorData}
          onRowClick={handleRowClick}
        />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">
          DWLR (Digital Water Level Recorder)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dwlrData.length > 0 ? (
            dwlrData.map((reading, index) => (
              <Card
                key={index}
                title={reading.title}
                value={reading.value}
                unit={reading.unit}
                timestamp={reading.timestamp}
                className="bg-gray-50"
              />
            ))
          ) : (
            <div className="text-gray-500">No DWLR data available</div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Flowmeter/Telemetry</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {flowmeterData.length > 0 ? (
            flowmeterData.map((reading, index) => (
              <Card
                key={index}
                title={reading.title}
                value={reading.value}
                unit={reading.unit}
                timestamp={reading.timestamp}
                className="bg-gray-50"
              />
            ))
          ) : (
            <div className="text-gray-500">No Flowmeter data available</div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Recent Readings History</h2>
        <Table columns={historyColumns} data={historyData} />
      </div>

      {/* <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Device Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Device ID
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={sensorData[0]?.monitoringUnitId || "DWLR-001"}
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Location
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={sensorData[0]?.location || "Main Borewell"}
                readOnly
              />
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {sensorData[0]?.status || "Active"}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Last Maintenance
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={sensorData[0]?.lastMaintenance || "2024-03-01"}
                readOnly
              />
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default Borewell;
